import { useCallback, useMemo, useState } from "react";

import type { AttributeReviewMappingRow } from "@/hooks/useAttributeReview.types";
import {
  COLLAPSED_BY_DEFAULT,
  GROUP_WORDS,
} from "@/lib/attributeReview/reviewGroupWords";
import { groupRows, isDoneRow } from "@/lib/attributeReview/reviewGroups";
import type { ReviewGroupKey } from "@/lib/attributeReview/reviewRow.types";

export function useReviewOrder(
  rows: AttributeReviewMappingRow[],
  queuedIds: ReadonlySet<string>,
) {
  const [closed, setClosed] = useState<Set<ReviewGroupKey>>(
    () => new Set(COLLAPSED_BY_DEFAULT),
  );
  const toggleGroup = useCallback(
    (key: ReviewGroupKey) =>
      setClosed((prev) => {
        const next = new Set(prev);
        if (!next.delete(key)) next.add(key);
        return next;
      }),
    [],
  );
  const setAllGroups = useCallback(
    (open: boolean) =>
      setClosed(
        new Set(open ? [] : (Object.keys(GROUP_WORDS) as ReviewGroupKey[])),
      ),
    [],
  );
  const { orderedIds, settledIds } = useMemo(() => {
    const ordered: string[] = [];
    const settled = new Set<string>();
    groupRows(rows, queuedIds).forEach(({ key, rows: members }) => {
      const ids = members.map((row) => String(row.id));
      members
        .filter((row) => isDoneRow(row, queuedIds))
        .forEach((row) => settled.add(String(row.id)));
      if (!closed.has(key)) ordered.push(...ids);
    });
    return { orderedIds: ordered, settledIds: settled };
  }, [rows, queuedIds, closed]);
  return { closed, toggleGroup, setAllGroups, orderedIds, settledIds };
}
